import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  AlertTriangle,
  Briefcase,
  Clock,
  Download,
  ExternalLink,
  Heart,
  Package,
  Sparkles,
} from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  UniversalTable,
  type UniversalTableColumn,
  Card,
  Button,
  StatusBadge,
  type StatusTone,
  NotificationCard,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
} from '@afrilink/ui';
import {
  adminReportsService,
  type ServicesReportData,
  type ServiceOrderRow,
  type MissedSlaRow,
} from '@/domains/reports/services/adminReportsService';
import { exportRowsAsCsv, todayStamp } from '@/shared/utils/csvExport';
import { formatDate, formatDateTime, formatZAR } from '@/shared/utils/format';

/**
 * /admin/reports/services  —  Services deep-dive
 *
 * Phase 10 Slice 10.3, T-10.3-07 + T-10.3-08.
 *
 * Single page that aggregates the four KPIs called out in BRD AC-10-16:
 *   - Total service orders (lifetime, all SKUs)
 *   - Studio orders lifetime (paid+ progressed only)
 *   - Outstanding balance at risk (Studio installments mid-recovery)
 *   - Missed-SLA count (Studio orders where slo_credit_issued_at is set)
 *
 * Plus two tables with CSV exports:
 *   - Service order list — filterable by SKU, status, and free-text search
 *     across the customer email and order ID. Click row → ServiceOrderDetailPage.
 *   - Missed-SLA log — ordered by slo_credit_issued_at desc, exposes the
 *     brief_completed_at vs delivered_at delta inline.
 *
 * Spec: _bmad-output/PHASE_10_SERVICES_BRD.md §6.5, AC-10-16
 *       _bmad-output/PHASE_10_SERVICES_IMPLEMENTATION_PLAN.md T-10.3-07/08
 */

const SKU_LABEL: Record<string, string> = {
  hosting: 'Hosting',
  'studio-starter': 'Studio Starter',
  'care-pro': 'Care Plan',
  'email-setup': 'Email Setup',
};

const STATUS_TONE: Record<string, StatusTone> = {
  pending_payment: 'pending',
  paid: 'paid',
  awaiting_consultation: 'pending',
  in_progress: 'info',
  delivered: 'info',
  complete: 'active',
  provisioning: 'pending',
  provisioning_domain: 'pending',
  failed_retryable: 'pending',
  failed_terminal: 'failed',
  cancelled: 'failed',
};

type SkuFilter = 'all' | 'hosting' | 'studio-starter' | 'care-pro' | 'email-setup';
type StatusFilter = 'all' | string;

export default function ServicesReportPage() {
  const [data, setData] = useState<ServicesReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [skuFilter, setSkuFilter] = useState<SkuFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminReportsService
      .getServicesReport()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const statusOptions = useMemo(() => {
    if (!data) return [] as string[];
    const set = new Set<string>();
    data.orders.forEach((o) => set.add(o.status));
    return Array.from(set).sort();
  }, [data]);

  const filteredOrders = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    return data.orders.filter((o) => {
      if (skuFilter !== 'all' && o.sku_code !== skuFilter) return false;
      if (statusFilter !== 'all' && o.status !== statusFilter) return false;
      if (needle) {
        const hay = `${o.customer_email ?? ''} ${o.id} ${o.primary_fqdn ?? ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [data, skuFilter, statusFilter, search]);

  function exportOrdersCsv() {
    const header = [
      'Order ID',
      'SKU',
      'Status',
      'Customer email',
      'Customer ID',
      'Primary FQDN',
      'Total paid (cents)',
      'Created at',
      'Delivered at',
      'Complete at',
      'SLO credit issued at',
    ];
    const rows = filteredOrders.map((o) => [
      o.id,
      o.sku_code,
      o.status,
      o.customer_email ?? '',
      o.customer_id,
      o.primary_fqdn ?? '',
      o.total_paid_cents,
      o.created_at,
      o.delivered_at ?? '',
      o.complete_at ?? '',
      o.slo_credit_issued_at ?? '',
    ]);
    exportRowsAsCsv(`services-orders-${todayStamp()}.csv`, header, rows);
  }

  function exportMissedSlaCsv() {
    if (!data) return;
    const header = [
      'Service order ID',
      'Customer email',
      'Customer ID',
      'Primary FQDN',
      'Brief completed at',
      'Delivered at',
      'SLO credit issued at',
    ];
    const rows = data.missed_sla_log.map((m) => [
      m.service_order_id,
      m.customer_email ?? '',
      m.customer_id,
      m.primary_fqdn ?? '',
      m.brief_completed_at,
      m.delivered_at ?? '',
      m.slo_credit_issued_at,
    ]);
    exportRowsAsCsv(`services-missed-sla-${todayStamp()}.csv`, header, rows);
  }

  const orderColumns = useMemo<UniversalTableColumn<ServiceOrderRow>[]>(
    () => [
      {
        key: 'id',
        header: 'Order',
        render: (o) => (
          <Link
            to={`/admin/services/${o.id}`}
            className="font-mono text-xs text-primary hover:underline"
          >
            {o.id.slice(0, 8)}
          </Link>
        ),
      },
      {
        key: 'sku',
        header: 'SKU',
        sortValue: (o) => o.sku_code,
        render: (o) => <span className="text-sm">{SKU_LABEL[o.sku_code] ?? o.sku_code}</span>,
      },
      {
        key: 'status',
        header: 'Status',
        sortValue: (o) => o.status,
        render: (o) => (
          <StatusBadge tone={STATUS_TONE[o.status] ?? 'pending'}>
            {o.status.replace(/_/g, ' ')}
          </StatusBadge>
        ),
      },
      {
        key: 'customer',
        header: 'Customer',
        render: (o) => (
          <span className="text-sm">{o.customer_email ?? o.customer_id.slice(0, 8)}</span>
        ),
      },
      {
        key: 'fqdn',
        header: 'Domain',
        render: (o) => (
          <span className="text-sm text-muted-foreground">{o.primary_fqdn ?? '—'}</span>
        ),
        hideOnMobile: true,
      },
      {
        key: 'paid',
        header: 'Paid',
        sortValue: (o) => o.total_paid_cents,
        render: (o) => (
          <span className="text-sm tabular-nums">{formatZAR(o.total_paid_cents / 100)}</span>
        ),
      },
      {
        key: 'created',
        header: 'Created',
        sortValue: (o) => new Date(o.created_at).getTime(),
        render: (o) => <span className="text-sm tabular-nums">{formatDate(o.created_at)}</span>,
        hideOnMobile: true,
      },
    ],
    [],
  );

  const missedSlaColumns = useMemo<UniversalTableColumn<MissedSlaRow>[]>(
    () => [
      {
        key: 'order',
        header: 'Order',
        render: (m) => (
          <Link
            to={`/admin/services/${m.service_order_id}`}
            className="font-mono text-xs text-primary hover:underline"
          >
            {m.service_order_id.slice(0, 8)}
          </Link>
        ),
      },
      {
        key: 'customer',
        header: 'Customer',
        render: (m) => (
          <span className="text-sm">{m.customer_email ?? m.customer_id.slice(0, 8)}</span>
        ),
      },
      {
        key: 'fqdn',
        header: 'Domain',
        render: (m) => (
          <span className="text-sm text-muted-foreground">{m.primary_fqdn ?? '—'}</span>
        ),
        hideOnMobile: true,
      },
      {
        key: 'brief',
        header: 'Brief done',
        sortValue: (m) => new Date(m.brief_completed_at).getTime(),
        render: (m) => <span className="text-xs tabular-nums">{formatDateTime(m.brief_completed_at)}</span>,
      },
      {
        key: 'delivered',
        header: 'Delivered',
        render: (m) => <span className="text-xs tabular-nums">{formatDateTime(m.delivered_at)}</span>,
      },
      {
        key: 'credit',
        header: 'Credit issued',
        sortValue: (m) => new Date(m.slo_credit_issued_at).getTime(),
        render: (m) => (
          <span className="text-xs tabular-nums text-warning-dark">
            {formatDateTime(m.slo_credit_issued_at)}
          </span>
        ),
      },
    ],
    [],
  );

  if (loading) {
    return <UniversalLoadingState variant="spinner" caption="Loading services report…" />;
  }

  if (error || !data) {
    return (
      <UniversalPage
        title="Services report"
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Reports', href: '/admin/reports' },
          { label: 'Services' },
        ]}
      >
        <NotificationCard
          tone="danger"
          title="Could not load services report"
          body={error ?? 'Unknown error'}
        />
      </UniversalPage>
    );
  }

  return (
    <UniversalPage
      title="Services report"
      description="Phase 10 deep-dive — service orders, installment recovery, missed-SLA log, outstanding balance."
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Reports', href: '/admin/reports' },
        { label: 'Services' },
      ]}
      actions={
        /* AR-9: reports report; they do not own the surface you WORK from.
           Until 2026-08-30 this page was the only route to a live service
           order, which is why /admin/services now exists. The order table
           below stays — it is genuine report data with a CSV export and an
           SLA lens — but the worklist is the place to action one. */
        <Button variant="outline" size="sm" asChild>
          <Link to="/admin/services">
            <Package className="h-4 w-4" /> Open worklist
          </Link>
        </Button>
      }
    >
      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi
          icon={Briefcase}
          label="Total service orders"
          value={data.total_service_orders.toString()}
          tone="primary"
        />
        <Kpi
          icon={Sparkles}
          label="Studio orders (lifetime)"
          value={data.studio_orders_lifetime.toString()}
          tone="primary"
        />
        <Kpi
          icon={AlertTriangle}
          label="Outstanding at-risk"
          value={formatZAR(data.outstanding_balance_at_risk_zar)}
          tone={data.outstanding_balance_at_risk_zar > 0 ? 'amber' : 'muted'}
        />
        <Kpi
          icon={Clock}
          label="Missed SLA (lifetime)"
          value={data.missed_sla_count.toString()}
          tone={data.missed_sla_count > 0 ? 'amber' : 'muted'}
        />
      </div>

      {/* Service order list */}
      <Card interactive={false}>
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
          <h2 className="font-display text-base font-semibold text-navy-primary">
            Service orders
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={exportOrdersCsv}
            disabled={filteredOrders.length === 0}
          >
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </header>

        <div className="grid grid-cols-1 gap-3 px-6 py-4 sm:grid-cols-3">
          <Select value={skuFilter} onValueChange={(v) => setSkuFilter(v as SkuFilter)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All SKUs</SelectItem>
              <SelectItem value="hosting">Hosting</SelectItem>
              <SelectItem value="studio-starter">Studio Starter</SelectItem>
              <SelectItem value="care-pro">Care Plan</SelectItem>
              <SelectItem value="email-setup">Email Setup</SelectItem>
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statusOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            placeholder="Search email, order ID, or domain…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="px-6 pb-2 pt-1 text-xs text-muted-foreground">
          Showing {filteredOrders.length} of {data.orders.length}
        </div>

        {filteredOrders.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-muted-foreground">
            No service orders match the current filters.
          </div>
        ) : (
          <div className="px-2 pb-4">
            <UniversalTable columns={orderColumns} data={filteredOrders} />
          </div>
        )}
      </Card>

      {/* Missed SLA log */}
      <Card interactive={false}>
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-4">
          <div>
            <h2 className="font-display text-base font-semibold text-navy-primary">
              Missed-SLA log
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Studio Starter orders where the 48-working-hour SLA was missed and
              the system auto-issued a 1-month free-hosting credit.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportMissedSlaCsv}
            disabled={data.missed_sla_log.length === 0}
          >
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </header>

        {data.missed_sla_log.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Heart className="mx-auto h-10 w-10 text-success" aria-hidden />
            <p className="mt-3 text-sm font-medium text-foreground">
              No SLA misses on record.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              The studio-slo-check cron sweeps hourly; anything appearing here
              is a genuine miss with a credit issued.
            </p>
          </div>
        ) : (
          <div className="px-2 pb-4">
            <UniversalTable columns={missedSlaColumns} data={data.missed_sla_log} />
          </div>
        )}
      </Card>

      {/* Cross-link to recovery queue */}
      <Card interactive={false} className="border-info/30 bg-info/5 p-4">
        <p className="text-sm text-foreground">
          Active installment recoveries (Track B escalation step 0–6) are managed
          on the dedicated{' '}
          <Link
            to="/admin/services/installment-recovery"
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            Installment recovery queue
            <ExternalLink className="h-3 w-3" />
          </Link>
          . The "outstanding at-risk" KPI on this page is the sum across that
          queue at this moment — refresh the page after acting on the queue to
          see it update.
        </p>
      </Card>
    </UniversalPage>
  );
}

function Kpi({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  tone: 'primary' | 'success' | 'amber' | 'muted';
}) {
  const valueClass =
    tone === 'amber'
      ? 'text-warning-dark'
      : tone === 'success'
        ? 'text-success'
        : tone === 'muted'
          ? 'text-muted-foreground'
          : 'text-foreground';
  const iconClass =
    tone === 'amber'
      ? 'text-warning-dark'
      : tone === 'success'
        ? 'text-success'
        : tone === 'muted'
          ? 'text-muted-foreground'
          : 'text-primary';
  return (
    <Card interactive={false} className="p-4">
      <div className="flex items-start justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <Icon className={`h-5 w-5 shrink-0 ${iconClass}`} aria-hidden />
      </div>
      <p className={`mt-2 text-2xl font-bold tabular-nums ${valueClass}`}>{value}</p>
    </Card>
  );
}
